import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    connect() {
        this.toolbar = document.getElementById('floating-toolbar')
        this.boundHandleSelection = this.handleSelection.bind(this)
        document.addEventListener('selectionchange', this.boundHandleSelection)
        document.addEventListener('focusin', this.boundHandleSelection)

        // Prevent toolbar from stealing focus
        if (this.toolbar) {
            this.toolbar.addEventListener('mousedown', (e) => {
                e.preventDefault()
            })
        }
    }

    disconnect() {
        document.removeEventListener('selectionchange', this.boundHandleSelection)
        document.removeEventListener('focusin', this.boundHandleSelection)
    }

    handleSelection() {
        const selection = window.getSelection()

        // Check if we are inside a page block editor
        let editable = null
        if (selection.rangeCount > 0) {
            const node = selection.anchorNode
            editable = node.nodeType === 3 ? node.parentElement.closest('[contenteditable="true"]') : node.closest('[contenteditable="true"]')
        } else if (document.activeElement.isContentEditable) {
            editable = document.activeElement
        }

        if (editable && document.getElementById('page-canvas').contains(editable)) {
            this.showToolbar(editable)
            this.currentEditable = editable
            // Persist the block-wrapper reference so settings can still be opened
            // after selection moves (e.g. clicking a toolbar button). Cleared only
            // when a different block is focused.
            this.currentBlockWrapper = editable.closest('.block-wrapper') || this.currentBlockWrapper
        } else {
            this.hideToolbar()
            this.currentEditable = null
        }
    }

    showToolbar(editableRect) {
        if (!this.toolbar) return

        // Enable pointer events
        this.toolbar.style.pointerEvents = 'auto'
        this.toolbar.classList.remove('hidden', 'opacity-0', '-translate-y-2')

        // Position toolbar
        const rect = editableRect.getBoundingClientRect()
        const canvasRect = document.getElementById('page-canvas').getBoundingClientRect()

        // Calculate relative position to canvas
        // Toolbar should default to BELOW the element to allow "Block Wrapper" header to show on top
        const toolbarHeight = this.toolbar.offsetHeight || 40
        let top = rect.bottom - canvasRect.top + 8

        // If off-screen bottom, flip to top (but maybe add more padding to avoid wrapper)
        // (Assuming wrapper header is ~30px tall)
        const canvasHeight = document.getElementById('page-canvas').offsetHeight
        if (top + toolbarHeight > canvasHeight) {
            top = rect.top - canvasRect.top - toolbarHeight - 8
        }

        // Center horizontally relative to block
        let left = (rect.left - canvasRect.left) + (rect.width / 2) - (this.toolbar.offsetWidth / 2)

        this.toolbar.style.top = `${top}px`
        this.toolbar.style.left = `${left}px`
    }

    hideToolbar() {
        if (!this.toolbar) return
        this.toolbar.style.pointerEvents = 'none'
        this.toolbar.classList.add('opacity-0', '-translate-y-2')
        // Wait for animation to hide
        setTimeout(() => {
            if (!this.currentEditable) this.toolbar.classList.add('hidden')
        }, 200)
    }

    // ==========================================
    // COMMANDS
    // ==========================================

    command(event) {
        event.preventDefault()
        const cmd = event.currentTarget.dataset.command
        document.execCommand(cmd, false, null)

        // Trigger input event to save
        if (this.currentEditable) {
            this.currentEditable.dispatchEvent(new Event('input', { bubbles: true }))
        }
    }

    align(event) {
        event.preventDefault()
        const alignment = event.currentTarget.dataset.alignment

        if (this.currentEditable) {
            // Option A: Use execCommand 'justifyLeft', etc. (Only works on block elements)
            // Option B: Set style directly on the editable wrapper.

            // Since our editable is the wrapper div, we can set style.textAlign
            this.currentEditable.style.textAlign = alignment

            // Also apply to parent for "tight" mode support
            if (this.currentEditable.parentElement) {
                this.currentEditable.parentElement.style.textAlign = alignment
            }

            // We also need to update the data-param "alignment" if that's how we save?
            // Note: The "Save" logic sends innerHTML. 
            // If we change style on the wrapper, innerHTML might NOT include that style 
            // UNLESS the wrapper IS the editable.
            // My partials: 
            // <div contenteditable="true" style="text-align: left; ..."> CONTENT </div>
            // So changing style.textAlign on currentEditable changes the element's style attribute.
            // BUT innerHTML is the CONTENT of the div, NOT the div itself.
            // The previous save logic sends `element.innerHTML`.
            // The style attribute is on the PARENT of the content... wait.

            // Let's check _heading.html.erb:
            // <div contenteditable="true" ... style="..."> TEXT </div>
            // If I change style.textAlign on THIS div, it affects the visual.
            // But what does save send?
            // It sends `value = element.innerHTML`.
            // The style is on the ELEMENT, not inside the innerHTML.
            // So saving 'innerHTML' DOES NOT save the alignment!

            // FIX: We need to send a separate update for alignment.
            // OR: We force `document.execCommand('justify' + alignment)`? 
            // `execCommand` usually wraps content in <div align="..."> which is dirty.

            // Better approach:
            // Trigger a specific save for 'alignment' field.
            // Does the API support it?
            // The PageBlock model has `alignment` field in content JSON.
            // So we should send `field: 'alignment', value: alignment`.

            // Find the page-editor controller instance
            const editorController = this.application.getControllerForElementAndIdentifier(
                this.currentEditable.closest('.block-wrapper'),
                'page-editor'
            )

            if (editorController) {
                editorController.sendUpdate('alignment', alignment)
            }
        }
    }

    openSettings(event) {
        event.preventDefault()
        event.stopPropagation()

        // Resolve the target block-wrapper, falling back through several
        // strategies because clicking the toolbar can clear selection state.
        const wrapper =
            this.currentBlockWrapper ||
            this.currentEditable?.closest('.block-wrapper') ||
            document.activeElement?.closest('.block-wrapper')

        if (!wrapper) {
            console.warn("[FloatingToolbar] openSettings: no active block-wrapper")
            return
        }

        const editorController = this.application.getControllerForElementAndIdentifier(
            wrapper,
            'page-editor'
        )
        if (editorController) {
            editorController.openBlockSettings()
            return
        }

        if (wrapper.dataset.blockUrl && window.loadBlockEditForm) {
            window.loadBlockEditForm(wrapper.dataset.blockUrl, wrapper)
        } else {
            console.warn("[FloatingToolbar] No way to open settings for wrapper", wrapper)
        }
    }
}
